import { useDispatch, useStore } from 'react-redux';
import { toast } from 'react-toastify';
import SDK from '../../../Components/SDK';
import { blockOfflineAction, updateMuteNotification } from '../../../Helpers/Utility';
import { formatUserIdToJid, formatGroupIdToJid } from '../../../Helpers/Chat/User';
import { getMessagesForReport, handleTempArchivedChats } from '../../../Helpers/Chat/ChatHelper';
import { getGroupData, getLatestGroupProfile } from '../../../Helpers/Chat/Group';
import {
    CHAT_TYPE_GROUP,
    CHAT_TYPE_SINGLE,
    MAX_PINNED_CHATS,
    REPORT_FROM_CONTACT_INFO,
    UNBLOCK_CONTACT_TYPE,
} from '../../../Helpers/Chat/Constant';
import { useLanguage } from '../../../localization/useLanguage';
import { T, TOAST } from '../../../localization/toastKeys';
import { updateBlockedContactAction } from '../../../Actions/BlockAction';
import {
    clearLastMessageinRecentChat,
    deleteActiveChatAction,
    updateArchiveStatusRecentChat,
    updateFavouriteStatusRecentChat,
    updateMuteStatusRecentChat,
    updatePinStatusRecentChat,
    updateUnreadFlagRecentChat,
} from '../../../Actions/RecentChatActions';
import logger from '../../../Helpers/logger';

/**
 * Side effects for the recent-chat context menu.
 *
 * NOTE: Every action guards offline, then accepts only statusCode 200 before
 * updating the store — nothing reports success until the server confirms it.
 *
 * @returns Action handlers for archive, mute, clear, delete, block and report.
 */
export default function useChatActions() {
    const dispatch = useDispatch();
    const store = useStore();
    const { t } = useLanguage();

    const jidOf = (chatId, chatType) =>
        chatType === CHAT_TYPE_GROUP ? formatGroupIdToJid(chatId) : formatUserIdToJid(chatId);

    // A rejected action must say so; staying silent looks identical to success.
    // The server message wins when present — it is more specific than any fallback.
    const reportFailure = (action, res, fallbackKey) => {
        logger.error(`${action} failed`, res);
        toast.error(res?.message || t(`${TOAST}.${fallbackKey}`));
    };

    const archiveChat = async (chatId, chatType, isArchived) => {
        if (blockOfflineAction()) return;
        const res = await SDK.updateArchiveChat(formatUserIdToJid(chatId, chatType), isArchived);
        if (res?.statusCode === 200) {
            dispatch(updateArchiveStatusRecentChat({ fromUserId: chatId, isArchived }));
            toast.success(t(`${TOAST}.${isArchived ? T.CHAT_ARCHIVED : T.CHAT_UNARCHIVED}`));
        } else {
            reportFailure('archiveChat', res, T.CHAT_UPDATE_FAILED);
        }
    };

    // Server enforces the cap too, and is the authority: the list is paginated, so the
    // store may not hold every pinned chat. Checking here only saves a round-trip.
    const pinChat = async (chatId, chatType, isPinned) => {
        if (blockOfflineAction()) return;
        if (isPinned) {
            const chats = store.getState()?.recentChatData?.data || [];
            const pinned = chats.filter(c => c.pinStatus === 1 && c.fromUserId !== chatId);
            if (pinned.length >= MAX_PINNED_CHATS) {
                toast.error(t(`${TOAST}.${T.PIN_LIMIT_REACHED}`, { count: MAX_PINNED_CHATS }));
                return;
            }
        }
        const res = await SDK.updatePinChat(jidOf(chatId, chatType), isPinned);
        if (res?.statusCode === 200) {
            dispatch(updatePinStatusRecentChat({ fromUserId: chatId, isPinned }));
            toast.success(t(`${TOAST}.${isPinned ? T.CHAT_PINNED : T.CHAT_UNPINNED}`));
        } else {
            reportFailure('pinChat', res, T.CHAT_PIN_FAILED);
        }
    };

    const favouriteChat = async (chatId, chatType, isFavourite) => {
        if (blockOfflineAction()) return;
        const res = await SDK.updateFavouriteChat(jidOf(chatId, chatType), isFavourite);
        if (res?.statusCode === 200) {
            dispatch(updateFavouriteStatusRecentChat({ fromUserId: chatId, isFavourite }));
            toast.success(t(`${TOAST}.${isFavourite ? T.ADDED_TO_FAVOURITES : T.REMOVED_FROM_FAVOURITES}`));
        } else {
            reportFailure('favouriteChat', res, T.FAVOURITES_UPDATE_FAILED);
        }
    };

    // Sticky flag the user sets by hand; unrelated to the unread counter the server keeps.
    const markChatUnread = async (chatId, chatType, isUnread) => {
        if (blockOfflineAction()) return;
        const res = await SDK.updateChatUnreadFlag(jidOf(chatId, chatType), isUnread);
        if (res?.statusCode === 200) {
            dispatch(updateUnreadFlagRecentChat({ fromUserId: chatId, isUnread }));
            toast.success(t(`${TOAST}.${isUnread ? T.MARKED_AS_UNREAD : T.MARKED_AS_READ}`));
        } else {
            reportFailure('markChatUnread', res, T.CHAT_UPDATE_FAILED);
        }
    };

    // muteDurationMs null means the mute has no end time; unmuting ignores it.
    const muteChat = async (chatId, chatType, isMuted, muteDurationMs = null) => {
        if (blockOfflineAction()) return;
        const res = await SDK.updateMuteNotification([jidOf(chatId, chatType)], isMuted, muteDurationMs);
        if (res?.statusCode === 200) {
            // The server counts from its own clock, so this is an estimate until the
            // next GET /muted replaces it with the authoritative value.
            const mutedUntil = isMuted && muteDurationMs ? Date.now() + muteDurationMs : null;
            const dispatchData = { fromUserId: chatId, isMuted, mutedUntil };
            updateMuteNotification(dispatchData);
            dispatch(updateMuteStatusRecentChat(dispatchData));
            toast.success(t(`${TOAST}.${isMuted ? T.CHAT_MUTED : T.CHAT_UNMUTED}`));
        } else {
            reportFailure('muteChat', res, T.NOTIFICATIONS_UPDATE_FAILED);
        }
    };

    const clearChat = async (chatId, chatType) => {
        if (blockOfflineAction()) return;
        const res = await SDK.clearChat(jidOf(chatId, chatType));
        if (res?.statusCode === 200) {
            dispatch(clearLastMessageinRecentChat({ fromUserId: chatId }));
            toast.success(t(`${TOAST}.${T.CHAT_CLEARED}`));
        } else {
            reportFailure('clearChat', res, T.CHAT_CLEAR_FAILED);
        }
    };

    const deleteChat = async (chatId, chatType) => {
        if (blockOfflineAction()) return;
        const res =
            chatType === CHAT_TYPE_GROUP
                ? await SDK.userDeleteGroup(formatGroupIdToJid(chatId))
                : await SDK.deleteChat(formatUserIdToJid(chatId));
        if (res?.statusCode === 200) {
            dispatch(deleteActiveChatAction({ fromUserId: chatId }));
            toast.success(t(`${TOAST}.${T.CHAT_DELETED}`));
        } else {
            reportFailure('deleteChat', res, T.CHAT_DELETE_FAILED);
        }
    };

    const blockChat = async (chatId, isBlocked) => {
        if (blockOfflineAction()) return;
        const userJid = formatUserIdToJid(chatId);
        const res = isBlocked ? await SDK.unblockUser(userJid) : await SDK.blockUser(userJid);
        if (res?.statusCode === 200) {
            handleTempArchivedChats(userJid, CHAT_TYPE_SINGLE);
            dispatch(updateBlockedContactAction(userJid, isBlocked ? UNBLOCK_CONTACT_TYPE : undefined));
            toast.success(t(`${TOAST}.${isBlocked ? T.USER_UNBLOCK_SUCCESS : T.USER_BLOCK_SUCCESS}`));
        } else {
            reportFailure('blockChat', res, T.BLOCK_LIST_UPDATE_FAILED);
        }
    };

    const leaveGroup = async (groupId, userJid, alsoDelete = false) => {
        if (blockOfflineAction()) return;
        const groupJid = formatGroupIdToJid(groupId);
        const res = await SDK.userExitGroup(groupJid, userJid);
        if (res?.statusCode !== 200) {
            reportFailure('leaveGroup', res, T.EXIT_GROUP_FAILED);
            return;
        }
        toast.info(t(`${TOAST}.${T.EXIT_GROUP_SUCCESS}`));
        if (!alsoDelete) return;

        const deleted = await SDK.userDeleteGroup(groupJid);
        if (deleted?.statusCode === 200) {
            dispatch(deleteActiveChatAction({ fromUserId: groupId }));
            toast.info(t(`${TOAST}.${T.GROUP_CHAT_DELETED}`));
        } else {
            reportFailure('leaveGroup:delete', deleted, T.EXIT_GROUP_DELETE_FAILED);
        }
    };

    // An empty <image> clears the stored one, so the server's current image is resent.
    // History stays on, as at every other setGroupProfile call site.
    const editChatProfile = async (chatId, { name, description }) => {
        if (blockOfflineAction()) return false;

        const groupJid = formatGroupIdToJid(chatId);
        if (!getGroupData(groupJid)) {
            reportFailure('editChatProfile', null, T.GROUP_UPDATE_FAILED);
            return false;
        }

        const { groupImage } = await getLatestGroupProfile(groupJid);
        const res = await SDK.setGroupProfile(groupJid, name, groupImage, true, description);
        if (res?.statusCode === 200) {
            toast.success(t(`${TOAST}.${T.GROUP_UPDATED}`));
            return true;
        }
        reportFailure('editChatProfile', res, T.GROUP_UPDATE_FAILED);
        return false;
    };

    const reportChat = async (chatId, chatType) => {
        if (blockOfflineAction()) return;
        const reportData = getMessagesForReport(chatId, REPORT_FROM_CONTACT_INFO);
        if (!reportData.length) {
            toast.error(t(`${TOAST}.${T.NO_MESSAGES_TO_REPORT}`));
            return;
        }
        const res = await SDK.reportUserOrGroup(jidOf(chatId, chatType), chatType, reportData);
        if (res?.statusCode === 200) {
            toast.success(t(`${TOAST}.${T.REPORT_SENT_SUCCESS}`));
        } else {
            reportFailure('reportChat', res, T.REPORT_SEND_FAILED);
        }
    };

    return {
        archiveChat,
        pinChat,
        favouriteChat,
        markChatUnread,
        muteChat,
        clearChat,
        deleteChat,
        blockChat,
        editChatProfile,
        reportChat,
        leaveGroup,
    };
}
